import { useEffect } from 'react'
import { auroraStore, useAuroraStore, type ViewKey } from './store/auroraStore'
import { activeChildId } from './store/selectors'
import { ConvexSync, getToken, setToken } from './sync/ConvexSync'
import { startHashRouter } from './sync/hashRouter'
import { useTheme } from './hooks/useTheme'
import { AppShell } from './components/AppShell'
import { Welcome } from './views/Welcome'
import { ThreadView } from './views/ThreadView'
import { TodayView } from './views/TodayView'
import { StoryView } from './views/StoryView'
import { StudioView } from './views/StudioView'
import { LibraryView } from './views/LibraryView'
import { ChildView } from './views/ChildView'
import { KidMode } from './views/KidMode'
import { CaptureLayer } from './components/capture/CaptureLayer'
import { ToastLayer } from './components/ToastLayer'
import { ErrorBoundary } from './components/ui/ErrorBoundary'
import { isLocalDemo, seedLocalDemo } from './lib/localDemo'

const CONVEX_URL = import.meta.env.VITE_CONVEX_URL ?? ''

const LABELS: Partial<Record<ViewKey, string>> = {
  thread: 'The thread',
  today: 'Today',
  story: 'Progress',
  studio: 'Studio',
  library: 'The library',
  child: 'Her page',
}

/**
 * The thread is the app; the other views are lenses on it.
 *
 * Kid Mode renders outside the shell — no navigation, nothing to wander into.
 */
export function App() {
  const view = useAuroraStore((s: any) => s.ui.view as ViewKey)
  const childId = useAuroraStore(activeChildId)
  const token = getToken()

  useTheme()
  useEffect(() => startHashRouter(), [])

  // Rehydrate the local demo on reload — it lives only in memory. `?demo=1`
  // enters it directly, which is also what headless screenshot runs use.
  useEffect(() => {
    const wantsDemo =
      isLocalDemo() || new URLSearchParams(window.location.search).get('demo') === '1'
    if (wantsDemo && !childId) {
      if (!getToken()) setToken('local-demo')
      seedLocalDemo()
    }
  }, [childId])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null
      const typing =
        el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return
      const map: Record<string, ViewKey> = {
        '1': 'thread', '2': 'today', '3': 'story', '4': 'studio', '5': 'library', '6': 'child',
      }
      const s = auroraStore.getState() as any
      if (map[e.key] && s.ui.view !== 'kid') s.setView(map[e.key])
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  if (!token) return <Welcome convexUrl={CONVEX_URL} />

  if (view === 'kid') {
    return (
      <>
        <ConvexSync convexUrl={CONVEX_URL} />
        <KidMode />
      </>
    )
  }

  return (
    <>
      {CONVEX_URL && <ConvexSync convexUrl={CONVEX_URL} />}
      <AppShell>
        {/* Keyed so a crashed lens recovers when you navigate away from it. */}
        <ErrorBoundary key={view} label={LABELS[view]}>
          {!childId ? (
            <Welcome convexUrl={CONVEX_URL} needsChild />
          ) : view === 'thread' ? (
            <ThreadView />
          ) : view === 'today' ? (
            <TodayView />
          ) : view === 'story' ? (
            <StoryView />
          ) : view === 'studio' ? (
            <StudioView />
          ) : view === 'child' ? (
            <ChildView />
          ) : (
            <LibraryView />
          )}
        </ErrorBoundary>
      </AppShell>
      {/* Capture is available over every lens — it is the most-used control. */}
      {view !== 'thread' && <CaptureLayer />}
      <ToastLayer />
    </>
  )
}
